import { useState } from 'react';
import { useAccount } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { FaUser, FaSignInAlt } from 'react-icons/fa';

export default function CustomConnectButton({
  className = '',
  showIcon = true,
  variant = 'primary',
  size = 'default',
  children
}) {
  const { isConnected, address } = useAccount();
  const { open } = useAppKit();
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      // Open the AppKit modal (works for both connect and account views)
      open();
    } catch (error) {
      console.error('Error opening AppKit modal:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  // Different button variants
  const getButtonStyles = () => {
    const baseStyles = "relative group flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-violet-500/50 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none";
    
    if (variant === 'primary') {
      return `${baseStyles} bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40`;
    } else if (variant === 'secondary') {
      return `${baseStyles} bg-gradient-to-r from-gray-800 to-gray-700 hover:from-gray-700 hover:to-gray-600 text-white border border-violet-500/30 hover:border-violet-500/50`;
    } else if (variant === 'outline') {
      return `${baseStyles} bg-transparent border-2 border-violet-500 hover:bg-violet-500/10 text-violet-400 hover:text-violet-300`;
    }
    return baseStyles;
  };

  const getConnectedStyles = () => {
    return "relative group flex items-center justify-between px-4 py-3 rounded-xl bg-gradient-to-r from-violet-600/20 to-purple-600/20 text-white border border-violet-500/30 hover:border-violet-500/50 transition-all duration-300 cursor-pointer";
  };

  if (isConnected && address) {
    return (
      <button
        onClick={handleConnect}
        className={`${getConnectedStyles()} ${className}`}
      >
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500 rounded-full mr-3 animate-pulse"></div>
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium">Connected</span>
            <span className="text-xs text-gray-400">
              {address.slice(0, 6)}...{address.slice(-4)}
            </span>
          </div>
        </div>
        {showIcon && <FaUser className="text-violet-400" />}
      </button>
    );
  }

  return (
    <button
      onClick={handleConnect}
      disabled={isConnecting}
      className={`${getButtonStyles()} ${className}`}
    >
      {/* Glow effect */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-violet-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
      
      {/* Button content */}
      <div className="relative flex items-center space-x-3">
        {isConnecting ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            <span>Connecting...</span>
          </>
        ) : (
          <>
            {showIcon && <FaSignInAlt className="text-lg" />}
            <span>Login / Signup</span>
          </>
        )}
      </div>
      
      {/* Shine effect */}
      <div className="absolute inset-0 rounded-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
      </div>
    </button>
  );
}
